import { Component, lazy, Suspense, useEffect, useRef, useState } from 'react';
import { PRELOAD_MARGIN } from './useNearViewport';

/*
 * The guard around a marketing page's lazily loaded chunks (a bento cell's
 * vignette, the genealogy story, the hero's phone): a chunk that fails to
 * load (a stale file name after a deploy, a dropped connection) or throws
 * while it renders leaves the caller's placeholder where it would have been,
 * instead of taking the whole page down with it. Suspense catches the wait,
 * never the failure.
 */

/**
 * The lazy component for `load`, one per attempt, kept for the page's
 * session so a boundary that mounts again (an Inertia visit back to the
 * page) renders the loaded chunk at once instead of its placeholder for a
 * tick. React.lazy keeps a failed load failed, so a failed attempt is
 * forgotten (forget): the next boundary to mount asks for the chunk afresh.
 * (A browser may keep a failed module fetch too, so that request, or the
 * retry's, can fail again without reaching the network: the placeholder
 * then stays.)
 */
const lazies = new Map();
function lazyFor(load, attempt) {
    const attempts = lazies.get(load) ?? new Map();
    lazies.set(load, attempts);
    if (!attempts.has(attempt)) attempts.set(attempt, lazy(load));

    return attempts.get(attempt);
}
function forget(load, attempt) {
    lazies.get(load)?.delete(attempt);
}

/** Catches what its children throw while rendering, and shows `fallback` instead. */
class Catch extends Component {
    constructor(props) {
        super(props);
        this.state = { failed: false };
    }

    static getDerivedStateFromError() {
        return { failed: true };
    }

    componentDidCatch(error) {
        if (import.meta.env.DEV) {
            console.error('[marketing] a lazily loaded part of the page failed; its placeholder stays', error);
        }
        // In production too: the page's global error handler (Sentry's) hears
        // of it, as it heard of a failed chunk before a boundary caught them.
        window.reportError?.(error);
        this.props.onFail();
    }

    render() {
        return this.state.failed ? this.props.fallback : this.props.children;
    }
}

/**
 * `fallback` is the placeholder the caller reserves (its exact box, so
 * nothing moves), shown while the chunk loads and for good if it fails.
 *
 * - With `load` (a dynamic import), the chunk's default export is loaded
 *   here and handed to `children(Component)`.
 * - Without it, `children` is the content to guard (a part loaded by other
 *   means, such as the hero's phone).
 *
 * A failure is reported (window.reportError, which the page's error
 * handler hears; also logged in development), and tried once more the next
 * time the placeholder comes near the viewport (within the same margin the
 * sections start loading at): it has to have been away first, so a chunk
 * that just failed is not asked for again at once. A second failure stays.
 */
export default function LazyBoundary({ load, fallback = null, children }) {
    const [attempt, setAttempt] = useState(0);
    const [failed, setFailed] = useState(false);
    const sentinel = useRef(null);
    const retrying = failed && attempt === 0;

    useEffect(() => {
        if (!retrying || !sentinel.current || typeof IntersectionObserver === 'undefined') return undefined;
        let away = false;
        const observer = new IntersectionObserver((entries) => {
            const near = entries.some((entry) => entry.isIntersecting);
            if (!near) away = true;
            else if (away) {
                setFailed(false);
                setAttempt(1);
            }
        }, { rootMargin: PRELOAD_MARGIN });
        observer.observe(sentinel.current);

        return () => observer.disconnect();
    }, [retrying]);

    const Chunk = load ? lazyFor(load, attempt) : null;

    return (
        <Catch
            key={attempt}
            onFail={() => {
                if (load) forget(load, attempt);
                setFailed(true);
            }}
            fallback={(
                <>
                    {fallback}
                    {/* Where the placeholder is, for the retry's observer: no
                        box of its own, at the placeholder's end. */}
                    {retrying && <span ref={sentinel} aria-hidden="true" data-lazy-sentinel="" className="pointer-events-none absolute size-px opacity-0" />}
                </>
            )}
        >
            {load ? <Suspense fallback={fallback}>{children(Chunk)}</Suspense> : children}
        </Catch>
    );
}
